import { useFleetStore } from '@/stores/fleet'
import { useMomentaryStore } from '@/stores/momentary'
import { myAxios, myAxiosRetry } from '@/services/myAxios'
import { getMwendBaseUrl } from '@/utils/api-config'
import { useDeviceStore } from '@/stores/device'
import { useRoutesStore } from '@/stores/routes'
import { useLogbookStore } from '@/stores/logbook'
import { AIS_CONFIG } from '@/utils/build-config'
import { fetchShipAisData, sanitizeImo } from '@/services/aisService'
import { getNavarea, getDeviceServices } from '@/helpers/apiServices'
import dayjs from 'dayjs'
import { loadJSON } from '@/utils/utils'

// Инициализируем хранилища Pinia
const getStores = () => {
  return {
    momentaryStore: useMomentaryStore(),
    fleetStore: useFleetStore(),
    deviceStore: useDeviceStore(),
    routesStore: useRoutesStore(),
    logbookStore: useLogbookStore(),
  }
}

// Хелпер для API-запросов
const makeApiRequest = async (url, method = 'get', data = null, customAxios = myAxios, config = {}) => {
  const { momentaryStore } = getStores()

  momentaryStore.setBusy(true)
  momentaryStore.setAPIError(false)

  try {
    const response = method === 'get' 
      ? await customAxios.get(url, config) 
      : await customAxios.post(url, data, config)

    momentaryStore.setBusy(false)
    return response.data
  } catch (error) {
    // Не устанавливаем ошибку для отмененных запросов
    if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
      momentaryStore.setAPIError(true)
    }
    momentaryStore.setBusy(false)
    throw error
  }
}

// Загружает данные кораблей (инсталляции)
export async function loadFleetInstallations(forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasInstallations) {
    console.log('Using cached installations data')
    return fleetStore.installations
  }

  try {
    const data = await makeApiRequest('api/v1/installations')

    const instECDIS = data.userInfos.filter(e => e.applicationId === 11 || e.applicationId === 101 || e.applicationId === 102 || e.applicationId === 105)

    if (instECDIS.length > 0) {
      fleetStore.addInstallations(instECDIS)
    }

    return fleetStore.installations
  } catch (error) {
    console.error('Failed to load fleet installations:', error)
    throw error
  }
}

// Загружает договоры для кораблей
export async function loadFleetAgreements(forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasAgreements) {
    console.log('Using cached agreements data')
    return fleetStore.agreements
  }

  try {
    const data = await makeApiRequest('api/v2/agreement')
    fleetStore.addAgreements(data)
    return fleetStore.agreements
  } catch (error) {
    console.error('Failed to load fleet agreements:', error)
    throw error
  }
}

// Загружает картинку для регистрации в телеграм
export async function loadQrServiceCode() {
  const { momentaryStore } = getStores()
  momentaryStore.setBusy(true)
  momentaryStore.setAPIError(false)
  try {
    // Используем myAxios напрямую, чтобы указать responseType: 'blob' для обработки изображения
    const response = await myAxios.get('api/v1/login/qr', { responseType: 'blob' })
    momentaryStore.setBusy(false)
    // Создаем URL для Blob, чтобы использовать его в <img>
    return URL.createObjectURL(response.data)
  } catch (error) {
    momentaryStore.setBusy(false)
    momentaryStore.setAPIError(true)
    console.error('Failed to load qr code:', error)
    return null
  }
}

function createEmptyFolioSummary(status = 'unknown') {
  return {
    status,
    hasAlert: false,
    charts: [],
    chartDetails: [],
    stats: {
      total: 0,
      expired: 0,
      expiringSoon: 0,
      healthy: 0,
    },
    updatedAt: new Date().toISOString(),
  }
}

function calculateItemDaysLeft(order, item) {
  if (item && typeof item.validDays === 'number' && !Number.isNaN(item.validDays)) {
    return item.validDays > 0 ? Math.floor(item.validDays) : 0
  }

  const expireCandidate = item?.expireAt || order?.expireAt
  if (expireCandidate) {
    const expireDate = dayjs(expireCandidate)
    if (expireDate.isValid()) {
      const today = dayjs().startOf('day')
      const diff = expireDate.startOf('day').diff(today, 'day')
      return diff > 0 ? diff : 0
    }
  }

  const monthCount = Number(item?.monthCount || order?.monthCount || 0)
  if (!monthCount || monthCount <= 0) return 0

  const createdCandidate = item?.createdUtc || order?.createdUtc || order?.createdAt || order?.created
  if (!createdCandidate) return 0

  const start = dayjs(createdCandidate)
  if (!start.isValid()) return 0

  const end = start.add(monthCount, 'month')
  const today = dayjs().startOf('day')
  const diff = end.startOf('day').diff(today, 'day')
  return diff > 0 ? diff : 0
}

function buildFolioSummary(deviceId, orders = []) {
  if (!Array.isArray(orders) || orders.length === 0) {
    return {
      ...createEmptyFolioSummary('no_data'),
      deviceId,
    }
  }

  const chartsMap = new Map()

  orders.forEach(order => {
    const createdUtc = order?.createdUtc || order?.createdAt || order?.created || null
    const createdWeight = createdUtc ? new Date(createdUtc).getTime() : Number.NEGATIVE_INFINITY
    const items = Array.isArray(order?.items) ? order.items : []

    items.forEach(item => {
      if (!item) return

      const type = typeof item.type === 'string' ? item.type.toLowerCase() : ''
      if (!type.includes('map')) return

      const id = item.id || item.chartId || item.name
      if (!id) return

      const key = `${type}::${id}`
      const daysLeft = calculateItemDaysLeft(order, item)

      const candidate = {
        id,
        type: item.type || '',
        daysLeft,
        createdUtc,
        createdWeight,
      }

      const existing = chartsMap.get(key)

      if (!existing) {
        chartsMap.set(key, candidate)
        return
      }

      if (candidate.daysLeft > existing.daysLeft) {
        chartsMap.set(key, candidate)
        return
      }

      if (candidate.daysLeft === existing.daysLeft && candidate.createdWeight > existing.createdWeight) {
        chartsMap.set(key, candidate)
      }
    })
  })

  const chartDetails = Array.from(chartsMap.values()).map(({ id, type, daysLeft, createdUtc }) => ({
    id,
    type,
    daysLeft,
    createdUtc,
  }))

  const total = chartDetails.length

  if (total === 0) {
    return {
      ...createEmptyFolioSummary('no_data'),
      deviceId,
    }
  }

  let expired = 0
  let expiringSoon = 0
  let healthy = 0

  chartDetails.forEach(chart => {
    const daysLeft = Number.isFinite(chart.daysLeft) ? chart.daysLeft : 0

    if (daysLeft <= 0) {
      expired += 1
    } else if (daysLeft <= 30) {
      expiringSoon += 1
    } else {
      healthy += 1
    }
  })

  let status
  if (healthy > 0) {
    status = 'healthy'
  } else if (expired === total) {
    status = 'expired_all'
  } else {
    status = 'expiring'
  }

  chartDetails.sort((a, b) => {
    if (b.daysLeft !== a.daysLeft) return b.daysLeft - a.daysLeft
    return a.id.localeCompare(b.id)
  })

  return {
    status,
    hasAlert: status === 'expired_all' || status === 'expiring',
    charts: chartDetails.map(chart => chart.id),
    chartDetails,
    stats: {
      total,
      expired,
      expiringSoon,
      healthy,
    },
    updatedAt: new Date().toISOString(),
    deviceId,
  }
}

// Загружает купленные карты (фолио) для корабля по deviceId
// Глобальный AbortController для отмены всех запросов primar-charts
let globalFolioAbortController = null;
// Сохраняем активный Promise evaluateFleetFolioStatuses
let activeFolioStatusEvaluation = null;

/**
 * Установить глобальный AbortController для отмены запросов
 * @param {AbortController} controller - Контроллер для отмены запросов
 */
export function setFolioAbortController(controller) {
  globalFolioAbortController = controller;
  
  // Если отменяем (controller = null), отменяем и активное выполнение
  if (!controller && activeFolioStatusEvaluation) {
    console.log('[Folio] Отменяем активное выполнение evaluateFleetFolioStatuses');
    activeFolioStatusEvaluation = null;
  }
}

/**
 * Получить данные Primar charts из API (fallback)
 * Преобразует {cells: [...]} в {orders: [...]} формат для совместимости
 * Использует дату окончания из поля exp в API
 */
async function getPrimarChartsData(deviceId) {
  console.log('[Folio] getPrimarChartsData: запрашиваем для:', deviceId);
  
  try {
    const config = globalFolioAbortController ? { signal: globalFolioAbortController.signal } : {};
    const response = await makeApiRequest(`${getMwendBaseUrl()}/api/v1/primar-charts/${deviceId}`, 'get', null, myAxiosRetry, config)
    console.log('[Folio] getPrimarChartsData: получен ответ, карт:', response?.cells?.length);

    if (!response || !Array.isArray(response.cells)) {
      console.log('[Folio] getPrimarChartsData: пустой или неверный ответ');
      return [];
    }

    // Преобразуем структуру данных
    const orders = response.cells.map(cell => {
      return {
        id: `primar-charts-${cell.id}`,
        deviceId: deviceId,
        isPaid: true,
        createdUtc: cell.exp,
        status: 'complete',
        items: [{
          id: cell.id,
          type: 's63 maps',
          expireAt: cell.exp,
          price: 0,
        }],
        paymentMethod: '',
        expiryDays: null,
      };
    });

    console.log('[Folio] getPrimarChartsData: преобразовано в', orders.length, 'заказов');
    return orders;
  } catch (error) {
    // Если запрос был отменен, не логируем как ошибку
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      console.log(`[Folio] getPrimarChartsData: запрос отменен для ${deviceId}`);
      return [];
    }
    console.error(`[Folio] getPrimarChartsData: ошибка для ${deviceId}:`, error);
    throw error;
  }
}

export async function loadFleetFolio(deviceId, forceRefresh = false, options = {}) {
  const { fleetStore } = getStores()

  if (!deviceId) {
    console.warn('loadFleetFolio: missing deviceId')
    return []
  }

  const { silent = false } = options

  if (!forceRefresh) {
    const cached = fleetStore.getDeviceFolio(deviceId)

    if (Array.isArray(cached) && cached.length > 0) {
      fleetStore.addFolio(deviceId, cached, { silent })
      const summary = buildFolioSummary(deviceId, cached)
      fleetStore.setFolioAlerts(deviceId, summary)
      return cached
    }
  }

  try {
    // Используем только fallback прямо на primar-charts API
    console.log('[Folio] loadFleetFolio: запрашиваем primar-charts для:', deviceId);
    let orders = await getPrimarChartsData(deviceId);
    console.log('[Folio] loadFleetFolio: получено:', orders.length, 'заказов');

    fleetStore.addFolio(deviceId, orders, { silent })
    const summary = buildFolioSummary(deviceId, orders)
    fleetStore.setFolioAlerts(deviceId, summary)

    return orders
  } catch (error) {
    console.error('[Folio] loadFleetFolio: ошибка при загрузке primar-charts:', error);
    
    fleetStore.setFolioAlerts(deviceId, {
      ...createEmptyFolioSummary('error'),
      deviceId,
    })

    return fleetStore.getDeviceFolio(deviceId)
  }
}

export async function evaluateFleetFolioStatuses(options = {}) {
  const { fleetStore } = getStores()
  const { forceRefresh = false } = options || {}

  const fleet = fleetStore.getFleet || []
  if (!Array.isArray(fleet) || fleet.length === 0) {
    return
  }

  // Сохраняем текущий Promise
  const evaluationPromise = (async () => {
    fleet.forEach(entry => {
      const deviceId = entry?.ship?.deviceId
      if (!deviceId) return

      fleetStore.setFolioAlerts(deviceId, {
        ...createEmptyFolioSummary('unknown'),
        deviceId,
      })
    })

    for (const entry of fleet) {
      const deviceId = entry?.ship?.deviceId
      if (!deviceId) continue

      // Проверяем, был ли AbortController отменен (значит, страница закрыта)
      if (!globalFolioAbortController) {
        console.log(`[Folio] Остановлен цикл evaluateFleetFolioStatuses, так как AbortController был отменен`);
        break;
      }

      try {
        await loadFleetFolio(deviceId, forceRefresh, { silent: true })
      } catch (error) {
        // Игнорируем ошибки отмены запросов
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          console.log(`[Folio] Folio status evaluation cancelled for device ${deviceId}`);
          continue;
        }
        console.error(`Failed to evaluate folio status for device ${deviceId}:`, error)
        fleetStore.setFolioAlerts(deviceId, {
          ...createEmptyFolioSummary('error'),
          deviceId,
        })
      }
    }
  })();

  activeFolioStatusEvaluation = evaluationPromise;
  
  try {
    await evaluationPromise;
  } finally {
    // Очищаем ссылку на завершившийся Promise
    if (activeFolioStatusEvaluation === evaluationPromise) {
      activeFolioStatusEvaluation = null;
    }
  }
}

function resolveImoForDevice(deviceId, fleetStore, initialImo) {
  const candidates = []

  if (initialImo) {
    candidates.push(initialImo)
  }

  const installation = Array.isArray(fleetStore.installations)
    ? fleetStore.installations.find(inst => inst.deviceId === deviceId)
    : null

  if (installation?.primarInfo?.shipInfo?.imo) {
    candidates.push(installation.primarInfo.shipInfo.imo)
  }

  if (installation?.imo) {
    candidates.push(installation.imo)
  }

  const fleetEntry = Array.isArray(fleetStore.fleet)
    ? fleetStore.fleet.find(entry => entry?.ship?.deviceId === deviceId)
    : null

  if (fleetEntry?.ship?.imo) {
    candidates.push(fleetEntry.ship.imo)
  }

  for (const candidate of candidates) {
    const sanitized = sanitizeImo(candidate)
    if (sanitized) {
      return sanitized
    }
  }

  return null
}

function buildPositionDataFromRemote(remoteData, fallbackImo) {
  if (!remoteData || typeof remoteData !== 'object') {
    return null
  }

  const positionInfo = remoteData.position || {}
  const latitude = Number(positionInfo.latitude)
  const longitude = Number(positionInfo.longitude)

  const lat = Number.isFinite(latitude) ? latitude : null
  const lon = Number.isFinite(longitude) ? longitude : null

  if (lat === null || lon === null) {
    return null
  }

  const speedValue = Number(positionInfo.speed)
  const courseValue = Number(positionInfo.course)

  const speedKnots = Number.isFinite(speedValue) ? speedValue : 0
  const course = Number.isFinite(courseValue) ? courseValue : 0

  const timestamp =
    positionInfo.timestamp ||
    remoteData.position_received_iso ||
    remoteData.update_time_iso ||
    remoteData.update_time ||
    new Date().toISOString()

  const destination = remoteData.destination || ''
  const etaValue = remoteData.eta_iso || remoteData.eta_utc || null

  const payload = {
    Mmsi: remoteData.mmsi || '',
    ImoNumber: remoteData.imoNumeric || fallbackImo || '',
    VesselName: remoteData.name || '',
    CallSign: remoteData.callsign || '',
    Position: { lat, lon },
    SpeedOverGround: {
      Knots: speedKnots,
      Ms: speedKnots * 0.514444,
      Kph: speedKnots * 1.852,
      Mph: speedKnots * 1.150779,
    },
    CourseOverGround: course,
    NavigationStatus: remoteData.navigational_status || '',
    Destination: destination,
    DateReceived: timestamp,
    Draught: remoteData.current_draught || remoteData.draught || '',
    ShipType: remoteData.vessel_type || '',
    TrueHeading: course,
  }

  const result = {
    lat,
    lon,
    time: timestamp,
    speedKnots,
    sog: speedKnots,
    cog: course,
    destination,
    eta: etaValue,
    payload,
    last_port: remoteData.last_port || '',
    navigational_status: remoteData.navigational_status || '',
  }

  if (remoteData.position_received_iso || remoteData.position_received) {
    result.position_received = remoteData.position_received_iso || remoteData.position_received
  }

  if (remoteData.image_full_url || remoteData.image_url) {
    result.image_url = remoteData.image_full_url || remoteData.image_url
  }

  result.remote_timestamp = remoteData.update_time_iso || remoteData.position_received_iso || timestamp

  return result
}

// Загружает AIS данные для корабля по mssId
export async function loadFleetAISData(forceRefresh = false) {
  const { fleetStore } = getStores()

  if (!AIS_CONFIG.USE_REMOTE_AIS_SERVICE) {
    console.warn('Remote AIS service disabled, skipping loadFleetAISData')
    return fleetStore.AISData
  }

  const existingKeys = Object.keys(fleetStore.AISData || {})

  if (!forceRefresh && existingKeys.length > 0) {
    console.log('Using cached AIS data')
    return fleetStore.AISData
  }

  const ships = fleetStore.getFleet || []

  if (!Array.isArray(ships) || ships.length === 0) {
    console.warn('loadFleetAISData: no ships available for AIS fetch')
    return fleetStore.AISData
  }

  const fetchTasks = ships.map(async entry => {
    const deviceId = entry?.ship?.deviceId
    if (!deviceId) {
      return
    }

    const initialImo = sanitizeImo(entry?.ship?.imo)
    const imo = resolveImoForDevice(deviceId, fleetStore, initialImo)

    if (!imo) {
      console.warn(`loadFleetAISData: skipping device ${deviceId} due to missing IMO`)
      return
    }

    try {
      const remoteData = await fetchShipAisData(imo, { forceRefresh })
      if (!remoteData) {
        return
      }

      const positionData = buildPositionDataFromRemote(remoteData, imo)
      if (!positionData) {
        console.warn(`loadFleetAISData: remote AIS for IMO ${imo} has no position coordinates`)
        return
      }

      if (!positionData.destination && remoteData.destination) {
        positionData.destination = remoteData.destination
      }

      if (!positionData.eta && (remoteData.eta_iso || remoteData.eta_utc)) {
        positionData.eta = remoteData.eta_iso || remoteData.eta_utc
      }

      fleetStore.addAISData(deviceId, positionData)
    } catch (error) {
      console.error(`loadFleetAISData: failed to fetch AIS for device ${deviceId} (IMO ${imo}):`, error)
    }
  })

  await Promise.all(fetchTasks)

  return fleetStore.AISData
}

// Загружает AIS данные для корабля по mssId
// export async function loadFleetAISData_old(deviceId, mssId, forceRefresh = false) {
//     const { fleetStore } = getStores();
//
//     if (!mssId)
//         return
//     // Проверяем, есть ли уже данные для этого устройства и не запрошено ли принудительное обновление
//     if (!forceRefresh && fleetStore.AISData[deviceId]) {
//         console.log("Using cached AIS data for device:", deviceId);
//         return fleetStore.AISData[deviceId];
//     }
//
//     try {
//         const data = await makeApiRequest(`api/v1/mss/vessel/${mssId}/location`, 'get', null, myAxiosRetry);
//         if (data && Object.keys(data).length > 0)
//             fleetStore.addAISData(deviceId, data);
//
//         return data;
//     } catch (error) {
//         console.error(`Failed to load AIS data for device ${deviceId}:`, error);
//         return null;
//     }
// }

// Загружает погоду для корабля по deviceId
export async function loadWeather(deviceId, forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные погоды для этого устройства и не запрошено ли обновление
  if (!forceRefresh && fleetStore.weather && fleetStore.weather[deviceId]) {
    console.log('Using cached weather data for device:', deviceId)
    return fleetStore.weather[deviceId]
  }

  try {
    // Сначала проверяем, есть ли данные AIS с координатами для этого корабя
    const aisData = fleetStore.AISData[deviceId]

    // Если нет данных AIS или отсутствуют координаты, мы не можем получить погоду
    if (!aisData || !aisData.lat || !aisData.lon) {
      console.log('No position data available for weather lookup for device:', deviceId)
      return null
    }

    // Используем текущее время для данных о погоде
    const currentTime = dayjs().unix()

    // Формируем URL для запроса погоды
    const weatherUrl = `https://nogotiles.bgeo.fi:6011/weather_info?lat=${aisData.lat}&lon=${aisData.lon}&t=${currentTime}`

    // Запрашиваем API погоды
    const weatherData = await makeApiRequest(weatherUrl, 'get', null, myAxiosRetry)

    if (weatherData) {
      // Сохраняем данные о погоде в хранилище
      fleetStore.weather = {
        ...fleetStore.weather,
        [deviceId]: {
          ...weatherData,
          timestamp: currentTime,
          coordinates: {
            lat: aisData.lat,
            lon: aisData.lon,
          },
        },
      }

      return fleetStore.weather[deviceId]
    }

    return null
  } catch (error) {
    console.error(`Failed to load weather data for device ${deviceId}:`, error)
    return null
  }
}

// Загружаем AIS данные для всех кораблей и затем отображаем их
export async function loadAllShipsAISDataAndDraw() {
return null

  const { fleetStore } = getStores()
  const ships = fleetStore.getFleet

  if (!ships || ships.length === 0) {
    console.log('No ships available to load AIS data')
    return Promise.resolve()
  }

  console.log('[AIS-DEBUG] Starting to load AIS data for', ships.length, 'ships')

  try {
    const data = await loadFleetAISData()
    console.log('[AIS-DEBUG] AIS data loaded successfully:', data)
    console.log('[AIS-DEBUG] fleetStore.AISData after loading:', fleetStore.AISData)
    return fleetStore.AISData
  } catch (err) {
    console.error(`Error loading AIS data:`, err)
    return null
  }

  // // Создаем массив промисов для загрузки AIS данных для каждого корабля
  // const aisPromises = ships.map(ship =>
  //     loadFleetAISData(ship.ship.deviceId, ship.ship.mssId)
  //         .catch(error => {
  //             console.error(`Error loading AIS data for ${ship.ship.deviceId}:`, error);
  //             return null; // Возвращаем null для неудачных запросов, чтобы Promise.all не завершился с ошибкой
  //         })
  // );
  //
  // // Ждем завершения всех промисов
  // await Promise.all(aisPromises);
  //
  // return fleetStore.AISData;
}

// Загружает транзакции (заказы) для всех кораблей
export async function loadFleetTransactions(forceRefresh = false, isBackground = false) {
  const { fleetStore, momentaryStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasOrders) {
    console.log('Using cached transactions data')
    return fleetStore.transactions
  }

  const loadingKey = 'fleet-transactions'
  const loadingLabel = isBackground ? 'Updating transactions...' : 'Loading transactions...'

  try {
    // Запускаем индикатор загрузки
    if (isBackground) {
      momentaryStore.startBackgroundLoading(loadingKey, loadingLabel)
    } else {
      momentaryStore.startCriticalLoading(loadingKey, loadingLabel)
    }

    const data = await makeApiRequest('api/v1/orders', 'get', null, myAxiosRetry)
    fleetStore.addOrders(data)

    // Останавливаем индикатор загрузки
    if (isBackground) {
      momentaryStore.stopBackgroundLoading(loadingKey)
    } else {
      momentaryStore.stopCriticalLoading(loadingKey)
    }

    return fleetStore.transactions
  } catch (error) {
    // Останавливаем индикатор загрузки в случае ошибки
    if (isBackground) {
      momentaryStore.stopBackgroundLoading(loadingKey)
    } else {
      momentaryStore.stopCriticalLoading(loadingKey)
    }

    console.error('Failed to load fleet transactions:', error)
    throw error
  }
}

// Загружает отгурзки для корабля по deviceId
export async function loadFleetDeliveries(deviceId, period, forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные отгрузок для этого устройства и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasDeliveries(deviceId)) {
    console.log('Using cached deliveries data for device:', deviceId)
    return fleetStore.deliveries
  }

  try {
    const requestData = {
      deviceIds: [deviceId],
      dateFrom: period,
    }

    const data = await makeApiRequest('api/v1/deliveries', 'post', requestData)

    let deliveries = []
    if (data.deliveries.length > 0) {
      deliveries = data.deliveries.filter(d => d.deviceId === deviceId)[0]?.deliveries || []
    }

    fleetStore.addDeliveries(deliveries)
    return fleetStore.deliveries
  } catch (error) {
    console.error('Failed to load fleet deliveries:', error)
    return fleetStore.deliveries
  }
}

// Загружает логбук корабля по deviceId
export async function loadFleetLogbook(deviceId) {
  const { fleetStore } = getStores()

  try {
    // Получаем все пользовательские файлы
    const data = await makeApiRequest(`api/v1/userfiles?deviceId=${deviceId}`)
    const { userFiles = [] } = data

    // Находим соответствующие файлы
    const webMessageRecord = userFiles.find(file => file.name === 'record' && file.type === 'logbook_record')

  const ecdisMessageRecord = userFiles.find(file => file.name === 'Update.txt' && file.type === 'message')

    // Создаем массив промисов для обработки данных
    const tasks = []

    if (webMessageRecord) {
      tasks.push(processWebMessages(webMessageRecord.id))
    }

    if (ecdisMessageRecord) {
      tasks.push(processEcdisMessages(ecdisMessageRecord.id, deviceId))
    }

    await Promise.allSettled(tasks)

    return fleetStore
  } catch (error) {
    console.error('Failed to load fleet logbook:', error)
    return fleetStore
  }
}

// Обрабатываем сообщения из логбука
async function processWebMessages(recordId) {
  try {
    const { fleetStore } = getStores()
    const messageData = await makeApiRequest(`api/v1/userfile/${recordId}`)

    fleetStore.addLogbook_web(messageData)
  } catch (error) {
    console.error('Failed to process logbook record:', error)
    return []
  }
}

// Обрабатываем сообщения из ECDIS
async function processEcdisMessages(messageId, deviceId) {
  const { fleetStore } = getStores()

  try {
    const messageData = await makeApiRequest(`api/v1/userfile/${messageId}`)

    const ecdisMessages = messageData.messages.map(message => ({
      source: 'ecdis',
      time: message.timestamp,
      hdg: message?.hdg ?? 0,
      rot: message?.rot ?? 0,
      cog: message?.cog ?? 0,
      sog: message?.sog ?? 0,
      message: message.message,
      pos: {
        lat: message?.lat ?? '',
        lon: message?.lon ?? '',
      },
      images: [],
      audio: {
        src: null,
        blob: null,
      },
      deviceId,
    }))

    fleetStore.addLogbook_ecdis(ecdisMessages)
  } catch (error) {
    console.error('Failed to process ECDIS messages:', error)
  }
}

// Загружает доступные карты по координатам точки
export async function loadMapsByCoordinates(position, providers = ['Primar']) {
  try {
    // Формируем данные для запроса
    const requestData = {
      Points: [
        {
          lat: position.lat,
          lon: position.lon,
        },
      ],
      Providers: providers,
    }

    // Выполняем запрос к API
    const data = await makeApiRequest('api/v1/maps-by-legs', 'post', requestData, myAxiosRetry)

    // Извлекаем названия ячеек из ответа и сортируем их по убыванию
    let cellNames = []

    if (data && data.length > 0) {
      data.forEach(providerData => {
        if (providerData.legsInfo && providerData.legsInfo.length > 0) {
          providerData.legsInfo.forEach(legInfo => {
            if (legInfo.mapIntersections && legInfo.mapIntersections.length > 0) {
              // Извлекаем id ячеек и добавляем их в массив
              const ids = legInfo.mapIntersections.map(intersection => intersection.id)
              cellNames = [...cellNames, ...ids]
            }
          })
        }
      })

      // Удаляем дубликаты и сортируем по убыванию
      cellNames = [...new Set(cellNames)].sort((a, b) => b.localeCompare(a))
    }

    return cellNames
  } catch (error) {
    console.error('Failed to load maps by coordinates:', error)
    return []
  }
}

// Проверяет наличие самой детальной карты для текущей позиции корабля
export async function checkShipFolioForPosition(deviceId) {
  const { fleetStore } = getStores()

  try {
    // Получаем AIS данные корабля с его текущей позицией
    const aisData = fleetStore.AISData[deviceId]

    // Если нет данных о позиции, выходим
    if (!aisData || !aisData.payload || !aisData.payload.Position) {
      console.log(`No position data for deviceId: ${deviceId}`)
      fleetStore.setFolioAlerts(deviceId, { hasAlert: false, charts: [] })
      return false
    }

    // Получаем координаты из данных AIS
    const position = aisData.payload.Position

    // Получаем список доступных карт для текущей позиции
    const availableCharts = await loadMapsByCoordinates(position)

    // Если нет доступных карт, выходим
    if (!availableCharts || availableCharts.length === 0) {
      console.log(`No available charts for deviceId: ${deviceId}`)
      fleetStore.setFolioAlerts(deviceId, { hasAlert: false, charts: [] })
      return false
    }

    // Находим самую детальную карту (с самой большой цифрой в 3-й позиции)
    let mostDetailedChart = availableCharts[0]
    for (const chart of availableCharts) {
      if (chart.length >= 3 && chart.charCodeAt(2) > mostDetailedChart.charCodeAt(2)) {
        mostDetailedChart = chart
      }
    }

    // Загружаем фолио для корабля, если оно еще не загружено
    if (!fleetStore.folio || fleetStore.folio.length === 0) {
      await loadFleetFolio(deviceId, true)
    }

    // Фильтруем купленные карты для этого корабля
    const paidItems = fleetStore.folio.filter(item => item.deviceId === deviceId && item.isPaid === true && item.status === 'complete' && !isExpired(item.expireAt))

    // Проверяем, есть ли самая детальная карта среди купленных и не истек ли срок
    let hasDetailedChart = false
    if (paidItems.length > 0) {
      for (const item of paidItems) {
        if (item.items && item.items.length > 0) {
          // Проверяем, содержит ли фолио самую детальную карту
          const hasChart = item.items.some(
            chartItem =>
              // chartItem.type === 'S63 maps' &&
              chartItem.id === mostDetailedChart
          )

          if (hasChart) {
            hasDetailedChart = true
            break
          }
        }
      }
    }

    // Сохраняем результат проверки и список доступных карт
    fleetStore.setFolioAlerts(deviceId, {
      hasAlert: !hasDetailedChart,
      charts: availableCharts,
      mostDetailed: mostDetailedChart,
    })

    return !hasDetailedChart // Возвращаем true если нужна карта (есть алерт)
  } catch (error) {
    console.error(`Error checking folio for deviceId ${deviceId}:`, error)
    return false
  }
}

// Проверяет, истек ли срок действия карты
// TODO
function isExpired() {
  // if (!expireDate) return true;
  // const now = new Date();
  // const expire = new Date(expireDate);
  // return now > expire;

  return false
}

// Проверяет наличие карт для всех кораблей с позициями
export async function checkAllShipsFolios() {
  const { fleetStore } = getStores()
  const ships = fleetStore.getFleet

  if (!ships || ships.length === 0) {
    console.log(`No available ships for checking charts`)
    return
  }

  // Фильтруем корабли, у которых есть AIS данные с позицией
  const shipsWithPosition = ships.filter(ship => {
    const deviceId = ship.ship.deviceId
    const aisData = fleetStore.AISData[deviceId]
    return aisData && aisData.payload && aisData.payload.Position
  })

  // Создаем массив промисов для проверки фолио каждого корабля
  const folioCheckPromises = shipsWithPosition.map(ship =>
    checkShipFolioForPosition(ship.ship.deviceId).catch(error => {
      console.error(`Error checking folio for ${ship.ship.deviceId}:`, error)
      return false
    })
  )

  // Ждем завершения проверки фолио для всех кораблей
  await Promise.all(folioCheckPromises)
  console.log(`Folio check completed for all ships`)
}

// Загружает данные AIO (Admiralty Information Overlay) из локального файла
export async function loadAIO() {
  const { fleetStore } = getStores()

  if (!fleetStore.hasAIO) {
    try {
      // Загружаем данные AIO из файла
      let aioData = await loadJSON('aio.json')
      if (!aioData) {
        throw new Error('Некорректный формат данных ADMIRALTY Information Overlay')
      }

      // Извлекаем временные и постоянные предупреждения
      let tempData = aioData.TempInfos ? aioData.TempInfos : []
      let permanentData = aioData.PermanentInfos ? aioData.PermanentInfos : []

      // Сохраняем данные в хранилище Pinia
      fleetStore.addAIO(tempData, permanentData)
      console.log('AIO data loaded successfully', { temp: tempData.length, permanent: permanentData.length })
      return
    } catch (error) {
      console.error('Error loading AIO data:', error)
      throw error
    }
  }
}

/**
 * Загружает данные Navarea из API для выбранного корабля
 * 
 * ВАЖНО: Navarea слой показывается только для кораблей с активной подпиской!
 * Поэтому мы загружаем данные ТОЛЬКО если есть выбранный device ID.
 * Без подписки у корабля нет доступа к данным и слой не будет показан.
 */
// Загружает Navarea данные один раз при инициализации
// Backend отдает ВСЕ данные без проверки подписки
// Frontend проверяет подписку для каждого корабля отдельно
export async function loadNavarea() {
  return []
  
  const { fleetStore } = getStores()

  // Если данные уже загружены - возвращаем кэш
  if (fleetStore.hasNavarea) {
    console.log('[loadNavarea] Navarea data already loaded (using cache)')
    return fleetStore.navarea || []
  }

  try {
    console.log('[loadNavarea] Loading Navarea data from API (global load, no device ID required)')
    const apiResponse = await getNavarea()
    
    // API возвращает массив данных напрямую
    let navareaData = []
    if (Array.isArray(apiResponse)) {
      navareaData = apiResponse
    } else if (apiResponse && apiResponse.results && Array.isArray(apiResponse.results)) {
      // Поддержка старого формата (если API вернёт объект с results)
      navareaData = apiResponse.results
    }
    
    if (navareaData && navareaData.length > 0) {
      // Сохраняем данные в хранилище
      fleetStore.addNavarea(navareaData)
      console.log('[loadNavarea] Navarea data loaded successfully', { count: navareaData.length })
      return navareaData
    } else {
      console.warn('[loadNavarea] API returned empty response')
      fleetStore.addNavarea([])
      return []
    }
  } catch (apiError) {
    console.error('[loadNavarea] Failed to load Navarea from API:', apiError.message)
    // Устанавливаем флаг даже при ошибке, чтобы не повторять попытку загрузки
    fleetStore.addNavarea([])
    return []
  }
}

// Загружает данные портов из локального файла
export async function loadPorts() {
  const { fleetStore } = getStores()

  if (!fleetStore.hasPorts) {
    try {
      // Загружаем данные ports из файла
      let portsData = await loadJSON('ports.json')
      if (!portsData) {
        throw new Error('Некорректный формат данных ports')
      }

      // Сохраняем данные в хранилище Pinia
      fleetStore.addPorts(portsData)
      console.log('Ports data loaded successfully', { count: portsData.length })
      return
    } catch (error) {
      console.error('Error loading ports data:', error)
      throw error
    }
  }
}
